import { setAuth, setCurrentUser, setFirstLogin, setLoadingAuth, setPolicyAcceptance, setUser } from './Actions/user.action';
import { Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Navigate } from 'react-router-dom';
import {api} from "./config";
import { getAccessToken } from './utils/accessToken';
import { isCookieAuthMode } from './utils/authMode';
import PolicyAcceptanceModal from './Scenes/utilisateurs/PolicyAcceptanceModal';

export const shouldGatePolicyAcceptance = (isAuthenticated, requiresPolicyAcceptance) =>
    Boolean(isAuthenticated && requiresPolicyAcceptance);

const PrivateRoute = ({ children }) => {
    const dispatch = useDispatch();
    const { isAuthenticated, loadingAuth, requires_policy_acceptance } = useSelector(state => state.users);
    const navigate = useNavigate();
    const [initialized, setInitialized] = useState(false);

    const checkAuth = useCallback(async ({ showSpinner = true, redirectOnMissingSession = true } = {}) => {
        const token = getAccessToken();

        // Aucun token local: terminer l'initialisation puis renvoyer au login.
        if (!token && !isCookieAuthMode()) {
            dispatch(setAuth(false));
            setInitialized(true);
            if (redirectOnMissingSession) {
                navigate("/");
            }
            return;
        }

        if (showSpinner) {
            dispatch(setLoadingAuth(true));
        }
        try {
            const response = await api.get(`users/is-auth`);

            const userData = response.data.data;
            if (response.data.success && userData?.is_authenticated) {
                dispatch(setAuth(true));
                dispatch(setUser(userData.user));
                dispatch(setFirstLogin(userData.user.first_login));
                dispatch(setPolicyAcceptance(userData.user));
                dispatch(setCurrentUser(userData.user));
            } else {
                dispatch(setAuth(false));
                if (redirectOnMissingSession) {
                    navigate("/");
                }
            }
        } catch {
            dispatch(setAuth(false));
            if (redirectOnMissingSession) {
                navigate("/");
            }
        } finally {
            if (showSpinner) {
                dispatch(setLoadingAuth(false));
            }
            setInitialized(true);
        }
    }, [dispatch, navigate]);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    useEffect(() => {
        const refreshAuthWhenVisible = () => {
            if (document.visibilityState === "visible" && initialized && isAuthenticated) {
                checkAuth({ showSpinner: false, redirectOnMissingSession: false });
            }
        };

        document.addEventListener("visibilitychange", refreshAuthWhenVisible);
        return () => document.removeEventListener("visibilitychange", refreshAuthWhenVisible);
    }, [checkAuth, initialized, isAuthenticated]);

    if (loadingAuth || !initialized) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    if (shouldGatePolicyAcceptance(isAuthenticated, requires_policy_acceptance)) {
        return <PolicyAcceptanceModal visible />;
    }

    return children;
};

export default PrivateRoute;
